/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import * as constants from './constants';

/** Safety margin kept against the token's own `exp`. */
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;
/** Fallback when the token carries no `exp`: the service refreshes its copy every 10 minutes. */
const SERVICE_TOKEN_WINDOW_MS = 9 * 60 * 1000;

/** Absolute expiry (ms) from a JWT `exp` claim, or 0 when the token carries none. */
const getJwtExpiryMs = (token) => {
  const payload = typeof token === 'string' ? token.split('.')[1] : null;
  if (!payload) {
    return 0;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const { exp } = JSON.parse(atob(padded));
    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
};

/**
 * Epoch ms until which a freshly fetched token may be reused.
 * `issuedAt` is `expiresIn` from /token/system, which is when the service minted the token rather
 * than when it expires, so it is only usable as a fallback anchor.
 */
const resolveSys2sysValidUntil = (token, issuedAt) => {
  const expiryMs = getJwtExpiryMs(token);
  if (expiryMs) {
    return expiryMs - EXPIRY_BUFFER_MS;
  }
  const mintedAt = Number(issuedAt) || 0;
  return mintedAt ? mintedAt + SERVICE_TOKEN_WINDOW_MS : 0;
};

/**
 * TokenHelper - Common utility for system-to-system token management
 * This is a standalone helper to avoid circular dependencies with other modules
 */
class TokenHelper {
  static sys2sysExpiry = 0;

  /** Epoch ms until which `sys2sysToken` may be reused without re-fetching. */
  static sys2sysValidUntil = 0;

  /** `forProd` the cached token was issued for — prod and non-prod tokens are not interchangeable. */
  static sys2sysTokenMode = null;

  static sys2sysToken = null;

  static pendingRequests = new Map();

  static requestCounter = 0;

  static awaitEventResult({ requestId, successEvent, errorEvent }) {
    return new Promise((resolve, reject) => {
      let unsubSuccess;
      let unsubError;

      const cleanup = () => {
        if (typeof unsubSuccess === 'function') unsubSuccess();
        if (typeof unsubError === 'function') unsubError();
      };

      const onSuccess = (event) => {
        if (event?.requestId !== requestId) return;
        cleanup();
        resolve(event?.data);
      };

      const onError = (event) => {
        if (event?.requestId !== requestId) return;
        cleanup();
        reject(new Error(event?.message || 'Request failed'));
      };

      unsubSuccess = Framework.getEventManager().on(successEvent, onSuccess);
      unsubError = Framework.getEventManager().on(errorEvent, onError);
    });
  }

  /**
   * Get system-to-system token with caching and request deduplication
   * @param {boolean} forProd - Whether to get production stack token
   * @param {boolean} forceRefresh - Force fetch new token even if cached
   * @returns {Promise<string|null>} The sys2sys token or null on error
   */
  static async getSys2sysToken(forProd = false, forceRefresh = false) {
    if (!forceRefresh && this.sys2sysValidUntil > Date.now() && this.sys2sysTokenMode === forProd) {
      return this.sys2sysToken;
    }

    try {
      const pendingKey = `sys2sys:${forProd ? 'prod' : 'default'}`;
      if (!forceRefresh && this.pendingRequests.has(pendingKey)) {
        return await this.pendingRequests.get(pendingKey);
      }

      this.requestCounter += 1;
      const requestId = `${Date.now()}_${this.requestCounter}`;

      const promise = (async () => {
        Framework.getEventManager().publish(constants.GET_SYS2SYS_TOKEN, {
          payload: { forProd, requestId }
        });

        const data = await this.awaitEventResult({
          requestId,
          successEvent: constants.GET_SYS2SYS_TOKEN_SUCCESS,
          errorEvent: constants.GET_SYS2SYS_TOKEN_ERROR
        });

        if (data?.data) {
          const { token, expiresIn } = data.data;
          this.sys2sysExpiry = expiresIn;
          this.sys2sysValidUntil = resolveSys2sysValidUntil(token, expiresIn);
          this.sys2sysToken = token;
          this.sys2sysTokenMode = forProd;
        }
        return this.sys2sysToken;
      })();

      this.pendingRequests.set(pendingKey, promise);
      const result = await promise;
      this.pendingRequests.delete(pendingKey);
      return result;
    } catch (err) {
      const notification = {
        open: true,
        message: 'Error fetching system token',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      return null;
    }
  }
}

export default TokenHelper;
